import { FormInstance } from 'antd/lib/form'
import {
    QCAttributeValueDefinition,
    QCAttributeValueType,
    UpdateFlatAttributeParams,
    UpdateWrappedAttributeParams,
} from 'Types/Attribute.types'
import {
    AttributeDefinitionType,
    ContainerType,
    ConversationResponseDataType,
    ConversationsDataType,
    JSONAnnotationDataType,
    NotificationPropsType,
    QCAttributesData,
    RatingsOnLinearConversationDataType,
    TaskAttribute,
    TaskMode,
    UpdatedQCAttributesData,
    UseCaseTypes,
} from 'Types/Tasks.types'
import { AttributeContext } from '../AttributeManager'
import {
    AttributeGroupConfigExtended,
    AttributeGroupEvent,
    BaseAttributeGroup,
} from './BaseAttributeGroup'
import { createReactElement } from 'shared/utils/react.utils'
import { QCAttributesBody } from 'Components/tasks/Attributes/QCAttributesBody'
import { convertFlatToWrappedAttributes } from 'Utils/attributes.utils'
import { isViewerMode } from 'Utils/task.utils'

export class QCAttributeGroup extends BaseAttributeGroup {
    groupKey = AttributeDefinitionType.QUALITY_EVALUATION
    attributeDefinitionType = AttributeDefinitionType.QUALITY_EVALUATION

    constructor(attributeContext: AttributeContext, config: AttributeGroupConfigExtended) {
        super(attributeContext, config)
        this.build()
    }

    build() {
        if (!this.attributes.length) {
            return
        }
        const editableAttributes = this.attributes.filter(attr => attr.is_value_editable)
        if (!editableAttributes.length) {
            return
        }
        const config = this.attributeContext.taskConfig
        const props = {
            attributes: editableAttributes,
            onUpdateAttribute: (attrName: string, val: QCAttributeValueType) => {
                const containerType =
                    this.attributeContext.taskState.getDraftOutputContainer()?.data.type
                if (
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION ||
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION_SUMMARISATION_USECASE ||
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION_RATING_USECASE ||
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.RLHF_RATING_RESPONSE_RATING ||
                    containerType === ContainerType.RatingsOnLinearConversation
                ) {
                    this.updateWrappedAttribute({
                        attrName,
                        attrValue: val,
                    })
                } else {
                    this.updateFlatAttribute({
                        attrName,
                        attrValue: val,
                    })
                }
                this.attributeContext.saveTask({
                    onComplete: (notifications?: NotificationPropsType) =>
                        this.attributeContext.studio.exitTask(notifications),
                })
                this.validate()
                this.render()
                this.emit(AttributeGroupEvent.Change)
                this.updateAreMandatoryAttributesFilled(this.getValues())
            },
            attributesOnUI: {},
            getValues: () => this.getValues(),
            setForm: (form: FormInstance) => {
                this.form = form
                // On init, validate the qc attributes form to set the initial disabled state of button
                this.attributeContext.studio.inputManager.addViolation({
                    id: 'qc-attributes',
                    type: 'error',
                    message: 'Please fill all the required details',
                })
            },
        }
        this.element = createReactElement(QCAttributesBody, {
            ...props,
            isReadOnly: () =>
                config.mode !== TaskMode.Triager && this.attributeContext.task.isReadOnly,
            mode: config.mode,
        })
    }

    validate = async () => {
        let isFormValid = true
        if (!this.attributes.length) {
            return isFormValid
        }
        const editableAttributes = this.attributes.filter(attr => attr.is_value_editable)
        if (!editableAttributes.length) {
            return isFormValid
        }
        const isFormTouched = this.form?.isFieldsTouched()
        if (!isFormTouched) {
            isFormValid = false
            return isFormValid
        }
        try {
            const isAttributesFormValid = await this.form?.validateFields().catch(() => false)
            if (!isAttributesFormValid) {
                isFormValid = false
            }
            this.attributeContext.studio.inputManager.removeViolation('qc-attributes')
        } catch (e) {
            this.attributeContext.studio.inputManager.addViolation({
                id: 'qc-attributes',
                type: 'error',
                message: 'Please fill all the required details',
            })
        }
        this.attributeContext.task.ui?.updater.forceUpdate()
        return isFormValid
    }

    private getContainer = () => {
        const containerType = this.attributeContext.taskState.getDraftOutputContainer()?.data.type
        if (containerType === ContainerType.RatingsOnLinearConversation) {
            const data = this.getContainerData() as RatingsOnLinearConversationDataType | undefined
            const versionId = data?.version_id
            if (versionId == null) return undefined
            return data?.quality_check?.evaluation_on_version_id?.[versionId] as
                | QCAttributesData
                | undefined
        }
        const data = this.getContainerData() as ConversationResponseDataType | undefined
        const responseVersionId = data?.response?.version_id
        if (responseVersionId == null) return undefined
        return data?.quality_check?.evaluation_on_response_version_id?.[responseVersionId] as
            | QCAttributesData
            | undefined
    }

    private updateWrappedAttribute = <T>({
        attrName,
        attrValue,
    }: UpdateWrappedAttributeParams<T>) => {
        const container = this.getContainer() ?? {}

        if (attrValue === undefined) {
            if (container[attrName]) {
                delete container[attrName]
            }
        } else {
            if (container[attrName]) {
                container[attrName].data.value = attrValue as NonNullable<QCAttributeValueType>
            } else {
                const editableAttributes = this.attributes.filter(attr => attr.is_value_editable)
                if (editableAttributes.length === 0) {
                    return
                }
                const containerType =
                    this.attributeContext.taskState.getDraftOutputContainer()?.data.type
                if (
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.RLHF_RATING_RESPONSE_RATING ||
                    containerType === ContainerType.RatingsOnLinearConversation
                ) {
                    this.initAttributesRatings(
                        attrName,
                        attrValue as NonNullable<QCAttributeValueType>,
                        editableAttributes
                    )
                } else {
                    this.initAttributes(
                        attrName,
                        attrValue as NonNullable<QCAttributeValueType>,
                        editableAttributes
                    )
                }
            }
        }
    }

    private updateFlatAttribute = <T>({ attrName, attrValue }: UpdateFlatAttributeParams<T>) => {
        const container = (this.getContainer() ?? {}) as unknown as UpdatedQCAttributesData

        if (attrValue === undefined) {
            if (container[attrName]) {
                delete container[attrName]
            }
        } else {
            if (container[attrName]) {
                container[attrName].value = attrValue as NonNullable<QCAttributeValueType>
            } else {
                this.initQCAttribute(
                    attrName,
                    attrValue as NonNullable<QCAttributeValueType>,
                    this.attributes
                )
            }
        }
    }

    // TODO: Remove this once BE migration is done
    private initAttributes(
        attrName: string,
        val: NonNullable<QCAttributeValueType>,
        attributes: TaskAttribute[]
    ) {
        const data = this.getContainerData() as ConversationResponseDataType

        if (!data.response.version_id) {
            return
        }

        if (!data.quality_check.evaluation_on_response_version_id) {
            data.quality_check.evaluation_on_response_version_id = {}
        }

        if (!data.quality_check.evaluation_on_response_version_id[data.response.version_id]) {
            data.quality_check.evaluation_on_response_version_id[data.response.version_id] = {}
        }

        data.quality_check.evaluation_on_response_version_id[data.response.version_id][attrName] = {
            data: {
                value: val,
                value_type: attributes.find(attr => attr.name === attrName)!.value_definition
                    .value_type as QCAttributeValueDefinition['value_type'],
            },
        }
    }

    // TODO: Remove this once BE migration is done
    private initQCAttribute(
        attrName: string,
        val: NonNullable<QCAttributeValueType>,
        attributes: TaskAttribute[]
    ) {
        const data = this.getContainerData() as JSONAnnotationDataType | ConversationsDataType

        if (!data.response.version_id) {
            return
        }

        if (!data.quality_check.evaluation_on_response_version_id) {
            data.quality_check.evaluation_on_response_version_id = {}
        }

        if (!data.quality_check.evaluation_on_response_version_id[data.response.version_id]) {
            data.quality_check.evaluation_on_response_version_id[data.response.version_id] = {}
        }

        data.quality_check.evaluation_on_response_version_id[data.response.version_id][attrName] = {
            value: val,
            value_type: attributes.find(attr => attr.name === attrName)!.value_definition
                .value_type as QCAttributeValueDefinition['value_type'],
        }
    }

    private initAttributesRatings(
        attrName: string,
        val: NonNullable<QCAttributeValueType>,
        attributes: TaskAttribute[]
    ) {
        const data = this.getContainerData() as RatingsOnLinearConversationDataType
        if (!data?.quality_check.evaluation_on_version_id || !data.version_id) {
            return
        }

        if (!data.quality_check.evaluation_on_version_id[data.version_id]) {
            data.quality_check.evaluation_on_version_id[data.version_id] = {}
        }

        data.quality_check.evaluation_on_version_id[data.version_id][attrName] = {
            data: {
                value: val,
                value_type: attributes.find(attr => attr.name === attrName)!.value_definition
                    .value_type as QCAttributeValueDefinition['value_type'],
            },
        }
    }

    getValues = (): QCAttributesData | undefined => {
        const config = this.attributeContext.taskConfig
        const containerType = this.attributeContext.taskState.getDraftOutputContainer()?.data.type

        let values: QCAttributesData | UpdatedQCAttributesData | undefined
        if (containerType === ContainerType.RatingsOnLinearConversation) {
            const data = this.getContainerData() as RatingsOnLinearConversationDataType
            const versionId = data.version_id
            values = (
                isViewerMode(config.mode)
                    ? Object.values(data.quality_check.evaluation_on_version_id ?? {})[0]
                    : versionId != null
                      ? data.quality_check.evaluation_on_version_id?.[versionId]
                      : undefined
            ) as QCAttributesData | UpdatedQCAttributesData | undefined
        } else {
            const data = this.getContainerData() as ConversationResponseDataType
            const responseVersionId = data.response.version_id
            values = (
                isViewerMode(config.mode)
                    ? Object.values(data.quality_check.evaluation_on_response_version_id ?? {})[0]
                    : responseVersionId != null
                      ? data.quality_check.evaluation_on_response_version_id?.[responseVersionId]
                      : undefined
            ) as QCAttributesData | UpdatedQCAttributesData | undefined
        }

        if (!values) {
            return undefined
        }

        if (Object.values(values as QCAttributesData)?.[0]?.data) {
            return values as QCAttributesData
        } else {
            return convertFlatToWrappedAttributes(values as UpdatedQCAttributesData)
        }
    }

    private getContainerData() {
        return this.attributeContext.taskState.getDraftOutputContainer()?.data.data as
            | ConversationResponseDataType
            | RatingsOnLinearConversationDataType
            | ConversationsDataType
    }

    removeAllAttributes() {
        const data = this.getContainerData() as ConversationResponseDataType
        if (!data) {
            return
        }
        const versionId = data.response.version_id
        if (versionId) {
            data.quality_check.evaluation_on_response_version_id = {
                ...data.quality_check.evaluation_on_response_version_id,
                [versionId]: {},
            }
        }
    }
}
